import { updateDiscordChannelApi } from "src/utils/api"
import { getBetTransactionData } from "src/utils/getBetTransactionData"
import type { TicketMarket } from "@/Overtime/types/markets"
import type { OvertimeBetData } from "src/utils/overtimeDiscordHelper"
import { formatCamelCaseToSpaces, formatTextToSlug } from "@/utils/helper"
import type { Chain } from "viem"
import { DiscordType } from "@/types/discord"

const updateOvertimeDiscordChannel = async (
  ticketMarkets: TicketMarket[],
  betAmount: string,
  totalOdds: number,
  chain: Chain | undefined,
  ticketId: string,
  betToken: string,
  selectedCollateralSymbol: string,
  isCombo?: boolean,
  isLive?: boolean
) => {
  try {
    const updatedItems: OvertimeBetData[] = ticketMarkets.map(
      (market: TicketMarket) => {
        // Transform sport enum to object structure
        const sportName = formatCamelCaseToSpaces(market.sport.toString())
        const sportSlug = formatTextToSlug(market.sport.toString())

        return {
          gameId: market.gameId,
          leagueId: Number(market.leagueId), // Convert League enum to number
          typeId: Number(market.typeId), // Convert MarketType enum to number
          position: market.position,
          homeTeam: market.homeTeam,
          awayTeam: market.awayTeam,
          chain: chain,
          ticketId: ticketId,
          line: market.line,
          playerId: market.playerProps?.playerId || 0,
          playerName: market.playerProps?.playerName || "",
          market: {
            homeTeam: market.homeTeam,
            awayTeam: market.awayTeam,
            leagueId: Number(market.leagueId),
            leagueName: market.leagueName,
            id: market.gameId,
            gameId: market.gameId,
            slug: formatTextToSlug(`${market.homeTeam}-${market.awayTeam}`),
            title: `${market.homeTeam} - ${market.awayTeam}`,
            participants: [
              { name: market.homeTeam },
              { name: market.awayTeam },
            ],
            startsAt: market.maturity,
            sport: {
              sportId: market.sport.toString(),
              name: sportName,
              slug: sportSlug,
            },
            league: {
              id: Number(market.leagueId),
              name: market.leagueName,
              slug: formatTextToSlug(market.leagueName),
              tournamentName: market.tournamentName,
            },
            country: {
              name: "International Tournaments",
              slug: "international-tournaments",
            },
            line: market.line,
            type: market.type,
            typeId: Number(market.typeId),
            odds: market.odds,
            positionNames: market.positionNames,
            // Add critical validation fields that were missing
            maturity: market.maturity,
            maturityDate: new Date(market.maturity * 1000).toISOString(),
            isOpen: market.isOpen !== undefined ? market.isOpen : true,
            isResolved:
              market.isResolved !== undefined ? market.isResolved : false,
            isCancelled:
              market.isCancelled !== undefined ? market.isCancelled : false,
            isPaused: market.isPaused !== undefined ? market.isPaused : false,
            isOneSideMarket: market.isOneSideMarket || false,
            isPlayerPropsMarket: market.isPlayerPropsMarket || false,
            isOneSidePlayerPropsMarket:
              market.isOneSidePlayerPropsMarket || false,
            isYesNoPlayerPropsMarket: market.isYesNoPlayerPropsMarket || false,
            playerProps: {
              playerId: market.playerProps?.playerId || 0,
              playerName: market.playerProps?.playerName || "",
            },
            combinedPositions: market.combinedPositions || [],
          },
          betAmount: betAmount,
          totalOdds: totalOdds,
          betToken: betToken,
          selectedCollateralSymbol: selectedCollateralSymbol,
          isCombo: isCombo,
          live: isLive,
        }
      }
    )

    const response = await updateDiscordChannelApi({
      items: updatedItems as any, // Type assertion needed as API expects AzuroSDK.BetslipItem[]
      type: DiscordType.Overtime,
    })

    return response
  } catch (error) {
    const err = error as Error
    console.error("Error updating Discord channel for Overtime", err)
    throw err
  }
}

export default updateOvertimeDiscordChannel
